import { LikeIcon } from 'components/icons/like-icon';
import InfiniteScroll, { InfiniteScrollRefType } from 'components/InfiniteScroll/InfiniteScroll';
import A from 'components/Link/Link';
import Loader from 'components/Loader/Loader';
import ProfileImage from 'components/ProfileImage/ProfileImage';
import Button from 'components/ui-kit/Button/Button';
import Modal from 'components/ui-kit/Modal/Modal';
import { SHOP } from 'config/fe-routes';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductService from 'services/product.service';
import UserService from 'services/user.service';
import ImageUrlResizer from 'utils/image-url-resizer';

import { useLikeAnimation } from '../../hooks/useLikeAnimation';
import { LikeTargetType } from '../../types/like-target.types';
import { GTM } from '../../utils/analytics';
import { LikeTarget } from '../../utils/like-target';
import MakeOfferModal from '../MakeOfferModal/MakeOfferModal';

interface LikesModalContainerProps {
  isOpen: boolean;
  isLiked: boolean;
  likeCount: number;
  targetId: number;
  productOrShopSlug: string;
  likesTargetType?: LikeTargetType;
  isSeller?: boolean;
  currentUserId?: number;
  onLikeToggle?: (liked: boolean) => void;
  onClose: () => void;
}

const userService = new UserService();
const productService = new ProductService();

export const LikesModal = ({
  isOpen,
  isLiked,
  likeCount,
  targetId,
  likesTargetType = LikeTarget.USER,
  productOrShopSlug,
  isSeller,
  currentUserId,
  onLikeToggle,
  onClose,
}: LikesModalContainerProps) => {
  const LIKES_PER_PAGE_LIMIT = 25;

  const listRef = useRef<HTMLDivElement>(null);
  const infiniteScrollRef = useRef<InfiniteScrollRefType>(null);
  const { ref: pillRef, isPopping: isPillPopping, trigger: triggerPillAnimation } = useLikeAnimation<HTMLButtonElement>();
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const { t } = useTranslation();

  const showOfferButton = LikeTarget.isProduct(likesTargetType) && !!isSeller;

  const [hasMoreLikes, setHasMoreLikes] = useState(true);
  const [likers, setLikers] = useState([]);

  const updateShadows = () => {
    const el = listRef.current;

    if (!el) {
      return;
    }
    setShowTopShadow(el.scrollTop > 0);
    setShowBottomShadow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  useEffect(() => {
    updateShadows();
  }, [likers]);
  const [isLoading, setLoading] = useState(true);
  const [selectedLikerId, setSelectedLikerId] = useState<number | null>(null);
  const [localLiked, setLocalLiked] = useState(isLiked);
  const [localLikeCount, setLocalLikeCount] = useState(likeCount);
  const isShopLikes = LikeTarget.isUser(likesTargetType);

  const title = isShopLikes ? t('pages.shop.likes-modal.followed-by') : t('pages.product.likes-modal.liked-by');

  const noLikersTitle = isShopLikes
    ? t('pages.shop.likes-modal.no-likes')
    : t(isSeller ? 'pages.shop.likes-modal.no-followers.own.title' : 'pages.shop.likes-modal.no-followers.title');
  const noLikersBody = isShopLikes || !isSeller ? undefined : t('pages.shop.likes-modal.no-followers.body');

  const loadMoreLikes = async (page: number) => {
    setLoading(true);

    const { list: userLikers, total } = await userService.getLikersList(likesTargetType, targetId, {
      offset: page * LIKES_PER_PAGE_LIMIT,
      limit: LIKES_PER_PAGE_LIMIT,
    });

    const combinedLikes = likers.concat(userLikers || []);

    setHasMoreLikes(() => !!userLikers?.length && total > combinedLikes.length);
    setLikers(() => combinedLikes);
    setLoading(false);
  };

  useEffect(() => {
    setLikers([]);
    setHasMoreLikes(true);
    setLocalLiked(isLiked);
    setLocalLikeCount(likeCount);
  }, [productOrShopSlug]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLocalLiked(isLiked);
    setLocalLikeCount(likeCount);
  }, [isOpen]);

  const handleLikeToggle = async () => {
    const newLiked = !localLiked;

    setLocalLiked(newLiked);
    setLocalLikeCount((prev) => (newLiked ? prev + 1 : prev - 1));

    if (newLiked) {
      triggerPillAnimation();
    }

    try {
      if (LikeTarget.isProduct(likesTargetType)) {
        await (newLiked ? productService.likeProduct(targetId) : productService.unlikeProduct(targetId));
      } else {
        await (newLiked ? userService.likeUser(targetId) : userService.unlikeUser(targetId));
      }

      if (newLiked) {
        setLikers([]);
        setHasMoreLikes(true);
        infiniteScrollRef.current?.reset();
      } else if (currentUserId) {
        setLikers((prev) => prev.filter((liker) => liker.id !== currentUserId));
      }

      onLikeToggle?.(newLiked);
    } catch {
      setLocalLiked(!newLiked);
      setLocalLikeCount((prev) => (!newLiked ? prev + 1 : prev - 1));
    }
  };

  const handleOfferSuccess = (likerId: number) => {
    setLikers((prev) => prev.map((liker) => (liker.id === likerId ? { ...liker, canStartOfferFlow: false } : liker)));
    setSelectedLikerId(null);
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        showCloseButton
        className="likes-modal"
        header={
          <div className="likes-modal__header">
            <span className="likes-modal__title">{title}</span>
            {isSeller ? (
              <div className="likes-modal__count-pill">
                <span>{localLikeCount}</span>
                <LikeIcon liked={true} />
              </div>
            ) : (
              <button
                ref={pillRef}
                className={`likes-modal__count-pill likes-modal__count-pill--toggle${isPillPopping ? ' likes-modal__count-pill--popping' : ''}`}
                onClick={handleLikeToggle}
              >
                <span>{localLikeCount}</span>
                <LikeIcon liked={localLiked} />
              </button>
            )}
          </div>
        }
        actions={
          <Button buttonType="primary" fullWidth size="large" onClick={onClose}>
            {t('common.close')}
          </Button>
        }
      >
        <div className="likes-modal__list-wrapper">
          {showTopShadow && <div className="likes-modal__shadow likes-modal__shadow--top" />}
          {showBottomShadow && <div className="likes-modal__shadow likes-modal__shadow--bottom" />}
          <div ref={listRef} className="likes-modal__list" onScroll={updateShadows}>
            <InfiniteScroll
              ref={infiniteScrollRef}
              batchSize={LIKES_PER_PAGE_LIMIT}
              dataLength={likers.length}
              loadMore={loadMoreLikes}
              hasMore={hasMoreLikes}
              loader={<Loader my={2} />}
              parentRef={listRef}
              resetDependencies={[productOrShopSlug]}
            >
              {!likers.length && !isLoading ? (
                <div className="likes-modal__empty">
                  <img src="/icons/icn-no-followers-love.svg" alt="" className="likes-modal__empty-icon" />
                  <p className="likes-modal__empty-title">{noLikersTitle}</p>
                  <p className="likes-modal__empty-body">{noLikersBody}</p>
                </div>
              ) : null}
              {likers.map((liker) => {
                const offerSent = liker.canStartOfferFlow === false;

                return (
                  <div className="likes-modal__row" key={'liker-' + liker?.shop?.active_slug}>
                    <A className="likes-modal__user no-style" {...SHOP(liker?.shop?.active_slug)} onClick={onClose}>
                      <ProfileImage src={ImageUrlResizer.resize(liker?.picture, 100)} />
                      <span className="likes-modal__username">@{liker?.shop?.active_slug}</span>
                    </A>

                    {showOfferButton && liker.id !== currentUserId && (
                      <button
                        className={`likes-modal__offer-btn${offerSent ? ' likes-modal__offer-btn--sent' : ''}`}
                        disabled={offerSent}
                        onClick={() => {
                          GTM.clickSellerMakeAnOfferButton(targetId);
                          setSelectedLikerId(liker.id);
                        }}
                      >
                        {offerSent ? t('common.offer-made') : t('common.make-offer')}
                      </button>
                    )}
                  </div>
                );
              })}
            </InfiniteScroll>
          </div>
        </div>
      </Modal>

      {selectedLikerId !== null && (
        <MakeOfferModal
          productId={targetId}
          buyerId={selectedLikerId}
          open={true}
          onClose={() => setSelectedLikerId(null)}
          onSuccess={() => handleOfferSuccess(selectedLikerId)}
          disableRedirect
        />
      )}
    </>
  );
};
